import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const TipDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  tipType,
  deliveryPartner,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showAllOrderIds, setShowAllOrderIds] = useState(false);
  const [orderBy, setOrderBy] = useState('tipAmount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amounts, order number, and tip percentage
      if (['paymentAmount', 'tipAmount', 'orderNumber', 'tipPercentage'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount, tipType, deliveryPartner]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const params = {
        businessDate,
        storeId,
        glAccount,
      };
      // Add tipType filter for 211700 accounts (cash vs credit)
      if (tipType) {
        params.tipType = tipType;
      }
      // Add deliveryPartner filter for 211600 DSP breakout rows.
      // Empty string is a valid filter (rows with no Cartwheel DSP match).
      if (deliveryPartner !== undefined) {
        params.deliveryPartner = deliveryPartner;
      }

      const response = await axios.get('/hd-api/gl-export/tip-details', {
        params,
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      // Add tipPercentage calculation to each row
      const dataWithPercentage = (response.data.data || []).map((row) => ({
        ...row,
        tipPercentage: row.paymentAmount > 0 ? (row.tipAmount / row.paymentAmount) * 100 : 0,
      }));

      setDetails(dataWithPercentage);
    } catch (err) {
      console.error('Error fetching tip details:', err);
      setError('Failed to load tip details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `${num.toFixed(1)}%`;
  };

  // Get color for tip percentage
  const getTipPercentageColor = (tipPercentage) => {
    if (tipPercentage >= 25) return 'success.main';
    if (tipPercentage >= 20) return 'success.light';
    if (tipPercentage >= 15) return 'warning.main';
    if (tipPercentage >= 10) return 'warning.dark';
    return 'error.main';
  };

  // Calculate totals
  const totals = useMemo(() => {
    const payment = filteredDetails.reduce((acc, d) => acc + (parseFloat(d.paymentAmount) || 0), 0);
    const tip = filteredDetails.reduce((acc, d) => acc + (parseFloat(d.tipAmount) || 0), 0);
    return {
      payment,
      tip,
      percentage: payment > 0 ? (tip / payment) * 100 : 0,
    };
  }, [filteredDetails]);

  // Count by handoff type
  const handoffCounts = useMemo(() => {
    return filteredDetails.reduce((acc, d) => {
      const handoff = d.handoffType || 'Unknown';
      acc[handoff] = (acc[handoff] || 0) + 1;
      return acc;
    }, {});
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => {
      const row = {
        'Order #': item.orderNumber,
        'Handoff': item.handoffType,
        'Provider': item.orderingProvider || 'In-Store',
        ...(glAccount === '211600' ? { 'DSP': item.deliveryPartner || '' } : {}),
        'Type': item.tipType,
        'Payment': parseFloat(item.paymentAmount) || 0,
        'Tip': parseFloat(item.tipAmount) || 0,
      };
      // Include all order IDs if checkbox is checked
      if (showAllOrderIds) {
        row['Order GUID'] = item.orderGuid || '';
        row['Check GUID'] = item.checkGuid || '';
        row['Olo ID'] = item.oloRef || '';
        row['ezCater ID'] = item.externalReference || '';
      }
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tips');
    XLSX.writeFile(wb, `tips-${storeId}-${businessDate}.xlsx`);
  };

  // Get category label based on GL account and tip type
  const getCategoryLabel = () => {
    if (glAccount === '211600') return 'Delivery Tips';
    if (glAccount === '211700') {
      if (tipType === 'cash') return 'Cash Tips Paid In';
      if (tipType === 'credit') return 'Credit Tips Mendo';
      return 'In-Store Tips';
    }
    return 'Tips';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VolunteerActivismIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {getCategoryLabel()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate} - GL {glAccount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {Object.entries(handoffCounts).map(([handoff, count]) => (
            <Chip
              key={handoff}
              icon={handoff === 'Delivery' ?
                <LocalShippingIcon sx={{ fontSize: 16 }} /> :
                <StorefrontIcon sx={{ fontSize: 16 }} />
              }
              label={`${count} ${handoff}`}
              size="small"
              color={handoff === 'Delivery' ? 'info' : 'secondary'}
              variant="outlined"
            />
          ))}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No tip details found for this selection.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showAllOrderIds && (
                      <>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'orderGuid'}
                            direction={orderBy === 'orderGuid' ? order : 'asc'}
                            onClick={() => handleRequestSort('orderGuid')}
                          >
                            Order GUID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'checkGuid'}
                            direction={orderBy === 'checkGuid' ? order : 'asc'}
                            onClick={() => handleRequestSort('checkGuid')}
                          >
                            Check GUID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'oloRef'}
                            direction={orderBy === 'oloRef' ? order : 'asc'}
                            onClick={() => handleRequestSort('oloRef')}
                          >
                            Olo ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'externalReference'}
                            direction={orderBy === 'externalReference' ? order : 'asc'}
                            onClick={() => handleRequestSort('externalReference')}
                          >
                            ezCater ID
                          </TableSortLabel>
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderNumber'}
                        direction={orderBy === 'orderNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderNumber')}
                      >
                        Order #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'handoffType'}
                        direction={orderBy === 'handoffType' ? order : 'asc'}
                        onClick={() => handleRequestSort('handoffType')}
                      >
                        Handoff
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderingProvider'}
                        direction={orderBy === 'orderingProvider' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderingProvider')}
                      >
                        Provider
                      </TableSortLabel>
                    </TableCell>
                    {glAccount === '211600' && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'deliveryPartner'}
                          direction={orderBy === 'deliveryPartner' ? order : 'asc'}
                          onClick={() => handleRequestSort('deliveryPartner')}
                        >
                          DSP
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'tipType'}
                        direction={orderBy === 'tipType' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'paymentAmount'}
                        direction={orderBy === 'paymentAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('paymentAmount')}
                      >
                        Payment
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'tipAmount'}
                        direction={orderBy === 'tipAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipAmount')}
                      >
                        Tip
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {showAllOrderIds && (
                      <>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['orderGuid'] || ''}
                            onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                fontSize: '0.75rem',
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 1,
                              },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['orderGuid'] && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => clearColumnFilter('orderGuid')}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['checkGuid'] || ''}
                            onChange={(e) => handleColumnFilterChange('checkGuid', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                fontSize: '0.75rem',
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 1,
                              },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['checkGuid'] && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => clearColumnFilter('checkGuid')}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['oloRef'] || ''}
                            onChange={(e) => handleColumnFilterChange('oloRef', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                fontSize: '0.75rem',
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 1,
                              },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['oloRef'] && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => clearColumnFilter('oloRef')}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['externalReference'] || ''}
                            onChange={(e) => handleColumnFilterChange('externalReference', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                fontSize: '0.75rem',
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 1,
                              },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['externalReference'] && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => clearColumnFilter('externalReference')}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderNumber'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderNumber'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('orderNumber')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['handoffType'] || ''}
                        onChange={(e) => handleColumnFilterChange('handoffType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['handoffType'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('handoffType')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderingProvider'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderingProvider', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderingProvider'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('orderingProvider')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    {glAccount === '211600' && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['deliveryPartner'] || ''}
                          onChange={(e) => handleColumnFilterChange('deliveryPartner', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              fontSize: '0.75rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              py: 0.5,
                              px: 1,
                            },
                            minWidth: 80,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['deliveryPartner'] && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearColumnFilter('deliveryPartner')}
                                  sx={{ p: 0.25 }}
                                >
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['tipType'] || ''}
                        onChange={(e) => handleColumnFilterChange('tipType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['tipType'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('tipType')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} align="right">
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['paymentAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('paymentAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['paymentAmount'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('paymentAmount')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} align="right">
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['tipAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('tipAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 0.5,
                            px: 1,
                          },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['tipAmount'] && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('tipAmount')}
                                sx={{ p: 0.25 }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.orderGuid}-${detail.checkGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showAllOrderIds && (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.orderGuid || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.checkGuid || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.oloRef || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.externalReference || '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell>{detail.orderNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={detail.handoffType === 'Delivery' ?
                            <LocalShippingIcon sx={{ fontSize: 14 }} /> :
                            <StorefrontIcon sx={{ fontSize: 14 }} />
                          }
                          label={detail.handoffType || 'Unknown'}
                          size="small"
                          color={detail.handoffType === 'Delivery' ? 'info' : 'secondary'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        {detail.orderingProvider || <Typography variant="caption" color="text.secondary">In-Store</Typography>}
                      </TableCell>
                      {glAccount === '211600' && (
                        <TableCell>
                          {detail.deliveryPartner || <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          icon={detail.tipType === 'CASH' ?
                            <PaymentsIcon sx={{ fontSize: 14 }} /> :
                            <CreditCardIcon sx={{ fontSize: 14 }} />
                          }
                          label={detail.tipType || 'Unknown'}
                          size="small"
                          color={detail.tipType === 'CASH' ? 'success' : 'info'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(detail.paymentAmount)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                        {formatCurrency(detail.tipAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} tip{filteredDetails.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showAllOrderIds}
                      onChange={(e) => setShowAllOrderIds(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show All Order IDs</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Payments
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(totals.payment)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tips
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.tip)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default TipDetailsDialog;
